import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

const API_BASE = process.env.REACT_APP_API_BASE || "https://enteai-web-lj55.vercel.app";

// --- FUTURISTIC BACKGROUND ---
function FuturisticBackground() {
  const { scrollY } = useScroll();

  const yGrid = useTransform(scrollY, [0, 1000], [0, -150]);
  const rotateGrid = useTransform(scrollY, [0, 1000], [0, 5]);
  const yOrb2 = useTransform(scrollY, [0, 1000], [0, -200]);
  const rotateRing = useTransform(scrollY, [0, 2000], [0, 360]);
  const smoothRotate = useSpring(rotateRing, { stiffness: 50, damping: 20 });

  return (
    <div className="fixed inset-0 overflow-hidden -z-10 bg-[#003049]">
      <div className="absolute inset-0 bg-gradient-to-br from-[#001824] via-[#003049] to-[#004e75]" />
      
      <motion.div
        style={{ y: yGrid, rotateX: rotateGrid }}
        className="absolute inset-[-50%] w-[200%] h-[200%] origin-top 
        bg-[linear-gradient(to_right,#457B9D22_1px,transparent_1px),
        linear-gradient(to_bottom,#457B9D22_1px,transparent_1px)]
        bg-[size:4rem_4rem] 
        [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]
        perspective-1000"
      />

      <motion.div
        style={{ y: yOrb2 }}
        className="absolute bottom-[10%] right-[5%] 
        w-[40vw] h-[40vw] bg-[#457B9D] 
        rounded-full blur-[140px] opacity-30 mix-blend-screen"
      />

      <motion.div
        style={{ rotate: smoothRotate }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[800px] h-[800px] 
        border border-[#D1D1D1]/10 rounded-full border-dashed pointer-events-none"
      />
    </div>
  );
}

// ----------------------------
// SLOT CONSTANTS
// ----------------------------
const FORNOON = { id: "forenoon", title: "Forenoon", subtitle: "10 AM — 1 PM" };
const AFTERNOON = { id: "afternoon", title: "Afternoon", subtitle: "2 PM — 5 PM" };

// ----------------------------
// MAIN COMPONENT
// ----------------------------
export default function Bookslot() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [projectTitle, setProjectTitle] = useState("");
  const [date, setDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // NEW: slot capacity from backend
  const [capacity, setCapacity] = useState({
    forenoon: { booked: 0, remaining: 2 },
    afternoon: { booked: 0, remaining: 2 }
  });
  const [loadingSlots, setLoadingSlots] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  // ----------------------------
  // FETCH SLOT CAPACITY FROM BACKEND
  // ----------------------------
  useEffect(() => {
    loadCapacity();
  }, [date]);

  async function loadCapacity() {
    if (!date) return;

    setLoadingSlots(true);
    try {
      const res = await fetch(`${API_BASE}/api/slot-availability?date=${date}`);
      const data = await res.json();

      if (data.success) {
        setCapacity(data.slots);
      }
    } catch (err) {
      console.error("Slot availability error:", err);
    }
    setLoadingSlots(false);
  }

  // ----------------------------
  // VALIDATION
  // ----------------------------
  function validate() {
    if (!name.trim()) return "Name is required.";
    if (!email.trim()) return "Email is required.";
    if (!phone.trim()) return "Phone number is required.";
    if (!projectTitle.trim()) return "Project title is required.";
    if (!date) return "Please select a date.";
    if (!selectedSlot) return "Please select a session.";
    if (capacity[selectedSlot].remaining <= 0) return "This slot is full.";
    return null;
  }

  // ----------------------------
  // SUBMIT BOOKING
  // ----------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    setMessage(null);

    const err = validate();
    if (err) return setMessage({ type: "error", text: err });

    const payload = {
      name,
      email,
      phone,
      projectTitle,
      date,
      slot: selectedSlot,
      notes
    };

    setLoading(true);

    try {
      const resp = await fetch(`${API_BASE}/api/book-slot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await resp.json().catch(() => null);

      if (resp.ok) {
        setMessage({
          type: "success",
          text: `Booking request for ${date} (${selectedSlot}) received. A confirmation will be sent to your email shortly.`,
          data: data.data,
        });

        setName("");
        setEmail("");
        setPhone("");
        setProjectTitle("");
        setNotes("");
        setSelectedSlot(null);
        loadCapacity(); // Re-fetch capacity to show updated count
      } else {
        setMessage({ type: "error", text: data?.message || "Booking failed." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }

    setLoading(false);
  }

  // ----------------------------
  // RENDER UI
  // ----------------------------
  return (
    <div className="font-sans min-h-screen pt-28 pb-12 px-4 relative text-gray-200">
      <FuturisticBackground />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-[#002639]/80 backdrop-blur-md border border-[#457B9D] rounded-2xl shadow-2xl overflow-hidden">

          {/* HEADER */}
          <div className="p-8 border-b border-[#457B9D] bg-[#003049]/50">
            <h1 className="text-2xl font-bold">
              Book a <span className="text-[#D1D1D1]">Slot</span>
            </h1>
          </div>

          {/* FORM */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">

            {/* SUCCESS OR ERROR MESSAGE */}
            {message && (
              <div
                className={`p-4 rounded-lg border ${
                  message.type === "error"
                    ? "bg-red-900/30 border-red-500 text-red-200"
                    : "bg-green-900/30 border-green-500 text-green-200"
                }`}
              >
                <strong>{message.type === "error" ? "Error" : "Success"}</strong>
                <p className="text-sm mt-1">{message.text}</p>
              </div>
            )}

            {/* NAME, EMAIL, PHONE */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                className="bg-[#003049] border border-[#457B9D] rounded-lg px-4 py-3"
                placeholder="Your full name"
                value={name} onChange={e => setName(e.target.value)}
              />
              <input
                className="bg-[#003049] border border-[#457B9D] rounded-lg px-4 py-3"
                placeholder="you@example.com"
                type="email"
                value={email} onChange={e => setEmail(e.target.value)}
              />
              <input
                className="bg-[#003049] border border-[#457B9D] rounded-lg px-4 py-3"
                placeholder="+91 9876543210"
                value={phone} onChange={e => setPhone(e.target.value)}
              />
            </div>

            {/* PROJECT TITLE */}
            <input
              className="w-full bg-[#003049] border border-[#457B9D] rounded-lg px-4 py-3"
              placeholder="Project Title"
              value={projectTitle} onChange={e => setProjectTitle(e.target.value)}
            />

            {/* DATE PICKER */}
            <input
              type="date"
              className="bg-[#003049] border border-[#457B9D] rounded-lg px-4 py-3 w-full"
              min={today}
              value={date} onChange={e => setDate(e.target.value)}
            />

            {/* SLOT SELECTION */}
            <div>
              <div className="mb-2 font-medium">Session</div>
              <div className="flex gap-4">

                {[FORNOON, AFTERNOON].map(s => {
                  const cap = capacity[s.id];
                  const full = cap.remaining <= 0;
                  const selected = selectedSlot === s.id;

                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={full}
                      onClick={() => !full && setSelectedSlot(s.id)}
                      className={`flex-1 p-3 rounded-lg border text-left transition-all ${
                        full
                          ? "bg-gray-800 text-gray-600 border-gray-700 cursor-not-allowed"
                          : selected
                          ? "bg-[#D1D1D1] text-[#003049]"
                          : "bg-[#003049] text-gray-300 border-[#457B9D] hover:border-[#D1D1D1]"
                      }`}
                    >
                      <div className="font-bold">{s.title}</div>
                      <div className="text-xs opacity-70">{s.subtitle}</div>

                      <div className="text-[10px] mt-1 opacity-60">
                        {loadingSlots ? "Checking..." : `${cap.booked}/2 booked`}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* NOTES */}
            <textarea
              rows={3}
              className="w-full bg-[#003049] border border-[#457B9D] rounded-lg px-4 py-3"
              placeholder="Notes (optional)"
              value={notes} onChange={e => setNotes(e.target.value)}
            />

            {/* BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 rounded-lg font-bold bg-[#D1D1D1] text-[#003049]"
            >
              {loading ? "Booking..." : "Book Slot"}
            </button>

          </form>
        </div>
      </motion.div>
    </div>
  );
}
